import intersection from 'lodash/intersection';
import { EntitlementsUser } from '@adsk/alloy-provider';
import { ALL_USERS_SPECIAL_ID } from '../constants/roles-types';
import { sortAlphabeticallyByPath } from './sortingHelpers';
import {
  Assignee,
  AssigneeType,
  EntityStatus,
  OxygenCompany,
  OxygenEntity,
  OxygenId,
  OxygenRole,
  OxygenUser,
} from '../sdk';
import { Oxygen } from '../providers/OxygenProvider';
import { translate } from './i18n';
import { EMPTY_ARRAY, HQUserStatus } from '../constants';

export const getAssigneeFromOxygenEntity = ({
  id,
  type,
}: Pick<OxygenEntity, 'id' | 'type'>): Assignee => ({ id, type });

/**
 * Get users list based on users, roles & companies.
 * This method returns a list of all users that either exists in assigneesArray or their role or company exits there.
 * @param assigneesArray - array of users, roles & companies.
 * @param oxygen
 */
export const getUsersList = (
  assigneesArray: Assignee[],
  oxygen: Pick<Oxygen, 'users'>,
): OxygenUser[] => {
  const userIds = assigneesArray
    .filter((obj) => obj.type === AssigneeType.User)
    .map((obj) => obj.id);
  const roleIds = assigneesArray
    .filter((obj) => obj.type === AssigneeType.Role)
    .map((obj) => obj.id);
  const companyIds = assigneesArray
    .filter((obj) => obj.type === AssigneeType.Company)
    .map((obj) => obj.id);

  return sortAlphabeticallyByPath(
    Object.keys(oxygen.users)
      .filter((userId) => {
        const user = oxygen.users[userId];
        return (
          userIds.includes(ALL_USERS_SPECIAL_ID) ||
          userIds.includes(userId) ||
          (user.company && companyIds.includes(user.company.oxygenId)) ||
          intersection(
            roleIds,
            user.roles.map((role: Record<string, string>) => role.oxygenId),
          ).length
        );
      })
      .map((userId) => oxygen.users[userId]),
    'name',
  );
};

export type SortAssigneesWithCurrentUserFirstParams = {
  assignees: Assignee[];
  oxygen: Oxygen;
  currentUser: Pick<EntitlementsUser, 'autodeskId'> | undefined;
  isTargetRfi?: boolean;
};

/** Example of sorted names order with current user "Jack Daniels":
 * Jack Daniels
 * Avi
 * Former member
 * simha-rotman@gmail.com
 * Tomer
 * Zach
 */
export const sortAssigneesWithCurrentUserFirst = ({
  assignees,
  oxygen,
  currentUser,
  isTargetRfi = false,
}: SortAssigneesWithCurrentUserFirstParams) => {
  if (oxygen.loading || !assignees.length || !currentUser) {
    return assignees;
  }

  const currentUserId = currentUser?.autodeskId;
  const assigneesWithoutCurrentUser = assignees.filter(({ id }) => id !== currentUserId);
  const sortedAssignees = sortAssigneesIncludingFormerAssigneesByName({
    oxygen,
    assignees: assigneesWithoutCurrentUser,
    isTargetRfi,
  });

  const isCurrentUserInAssignees = assigneesWithoutCurrentUser.length < assignees.length;
  return isCurrentUserInAssignees
    ? [{ id: currentUserId as string, type: AssigneeType.User }, ...sortedAssignees]
    : sortedAssignees;
};

export const getFormerUser = (id: OxygenId): OxygenUser =>
  ({
    id,
    autodeskId: id,
    name: translate('formerUser'),
    oxygenType: AssigneeType.User,
    type: AssigneeType.User,
    imageUrl: '',
    company: { id: '', name: '', oxygenId: '' },
    status: EntityStatus.Deleted,
  } as OxygenUser);

export const getBridgeUser = (id: OxygenId): OxygenUser =>
  ({
    id,
    autodeskId: id,
    name: translate('bridgeUser'),
    oxygenType: AssigneeType.User,
    type: AssigneeType.User,
    imageUrl: '',
    company: { id: '', name: '', oxygenId: '' },
    status: EntityStatus.Active,
  } as OxygenUser);

export const getFormerRole = (id: OxygenId): OxygenRole =>
  ({
    id,
    name: translate('formerRole'),
    oxygenType: AssigneeType.Role,
    type: AssigneeType.Role,
    status: EntityStatus.Inactive,
  } as OxygenRole);

export const getBridgeRole = (id: OxygenId): OxygenRole =>
  ({
    id,
    name: translate('bridgeRole'),
    oxygenType: AssigneeType.Role,
    type: AssigneeType.Role,
    status: EntityStatus.Active,
  } as OxygenRole);

export const getFormerCompany = (id: OxygenId): OxygenCompany =>
  ({
    id,
    name: translate('formerCompany'),
    oxygenType: AssigneeType.Company,
    type: AssigneeType.Company,
    status: EntityStatus.Deleted,
  } as OxygenCompany);

export const getBridgeCompany = (id: OxygenId): OxygenCompany =>
  ({
    id,
    name: translate('bridgeCompany'),
    oxygenType: AssigneeType.Company,
    type: AssigneeType.Company,
    status: EntityStatus.Active,
  } as OxygenCompany);

export type mapAssigneesToOxygenObjectsParams = {
  oxygen: Oxygen;
  assignees?: Assignee[] | null;
  isTargetRfi?: boolean;
};

export const mapAssigneesToOxygenObjects = ({
  oxygen,
  assignees,
  isTargetRfi = false,
}: mapAssigneesToOxygenObjectsParams): OxygenEntity[] =>
  assignees?.map(({ id, type }) => {
    switch (type) {
      case AssigneeType.User: {
        if (oxygen.users?.[id]) {
          return oxygen.users[id];
        }

        return isTargetRfi ? getBridgeUser(id) : getFormerUser(id);
      }
      case AssigneeType.Role: {
        if (oxygen.roles?.[id]) {
          return oxygen.roles[id];
        }

        return isTargetRfi ? getBridgeRole(id) : getFormerRole(id);
      }
      case AssigneeType.Company: {
        if (oxygen.companies?.[id]) {
          return oxygen.companies[id];
        }

        return isTargetRfi ? getBridgeCompany(id) : getFormerCompany(id);
      }
      default:
        return isTargetRfi ? getBridgeUser(id) : getFormerUser(id);
    }
  }) || EMPTY_ARRAY;

export type GetAssigneeDisplayNameParams = {
  oxygen: Oxygen;
  assignee: Assignee;
  isTargetRfi?: boolean;
};

export const getAssigneeDisplayName = ({
  oxygen,
  assignee,
  isTargetRfi = false,
}: GetAssigneeDisplayNameParams): string => {
  if (oxygen.loading) {
    return '';
  }

  const [oxygenEntity] = mapAssigneesToOxygenObjects({
    oxygen,
    assignees: [assignee],
    isTargetRfi,
  });

  const isDeletedUser =
    assignee.type === AssigneeType.User && oxygenEntity.status === HQUserStatus.DELETED;
  if (isDeletedUser) {
    return translate('REMOVED_USER_FORMAT', {
      username: oxygenEntity.name,
    });
  }

  return oxygenEntity.name;
};

export type GetAssigneeCompanyNameParams = {
  oxygen: Oxygen;
  assignee: Assignee;
  isTargetRfi?: boolean;
};

export const getAssigneeCompanyName = ({
  oxygen,
  assignee,
  isTargetRfi = false,
}: GetAssigneeCompanyNameParams): string => {
  if (assignee.type !== AssigneeType.User || oxygen.loading) {
    return '';
  }

  const [userEntity] = mapAssigneesToOxygenObjects({
    oxygen,
    assignees: [assignee],
    isTargetRfi,
  }) as OxygenUser[];

  return userEntity.status === HQUserStatus.DELETED ? '' : userEntity.company?.name ?? '';
};

export type GetAssigneeDisplayNameWithCompanyNameParams = {
  oxygen: Oxygen;
  assignee: Assignee;
  isTargetRfi?: boolean;
};

export const getAssigneeDisplayNameWithCompanyName = ({
  oxygen,
  assignee,
  isTargetRfi = false,
}: GetAssigneeDisplayNameWithCompanyNameParams): string => {
  if (oxygen.loading) {
    return '';
  }

  const displayName = getAssigneeDisplayName({ oxygen, assignee, isTargetRfi });
  const companyName = getAssigneeCompanyName({ oxygen, assignee, isTargetRfi });
  return companyName ? `${displayName} (${companyName})` : displayName;
};

export const getAssigneeImageUrl = (oxygen: Oxygen, assignee: Assignee): string => {
  if (oxygen.loading) {
    return '';
  }

  const [oxygenEntity] = mapAssigneesToOxygenObjects({ oxygen, assignees: [assignee] });
  if (oxygenEntity.type === AssigneeType.Role) {
    return '';
  }

  return oxygenEntity.imageUrl || '';
};

export type SortAssigneesIncludingFormerAssigneesByNameParams = {
  oxygen: Oxygen;
  assignees?: Assignee[] | null;
  isTargetRfi?: boolean;
};

/** Example of sorted names order:
 * Avi
 * Bridge User
 * Former member
 * simha-rotman@gmail.com
 * Tomer
 * Zach
 */
export const sortAssigneesIncludingFormerAssigneesByName = ({
  oxygen,
  assignees,
  isTargetRfi = false,
}: SortAssigneesIncludingFormerAssigneesByNameParams): Assignee[] => {
  if (oxygen.loading || !assignees?.length) {
    return assignees || EMPTY_ARRAY;
  }

  const oxygenEntities = mapAssigneesToOxygenObjects({ oxygen, assignees, isTargetRfi });
  const sortedOxygenEntities = sortAlphabeticallyByPath(oxygenEntities, 'name');
  return sortedOxygenEntities.map(getAssigneeFromOxygenEntity);
};

export const getIdsFromAssignees = (assignees: Assignee[]) => assignees.map(({ id }) => id);
